import { useEffect, useRef, useState } from 'react'
import type { MarbleGame } from 'src/types/domain/Game'
import { getLatestEvent } from 'src/utils/raceUtils'
import { isCrashGameType } from 'src/utils/gameTypeUtils'
// Crash-curve contract (CRASH_RATE ≈ 0.05997, CRASH_REVEAL_DELAY_SECONDS = 3)
// imported from the shared leaf module (single source of truth). It MUST match
// the 155-game-crash engine config and the marbles-stack-go backend
// `revealDelaySeconds` EXACTLY — all three repos share this contract.
//
// The reveal delay keeps the displayed multiplier FLAT at 1.00x for its first
// seconds after race_started, closing the "bet grace" exploit: the platform
// counts a bet if betCreatedAt <= race_started + ~2s, so without a delay a
// player could watch the multiplier climb (confirm the round did NOT
// instant-crash at 1.00x) and still bet inside the grace window for positive
// EV. Flattening the early curve removes that signal.
import { CRASH_RATE, CRASH_REVEAL_DELAY_SECONDS } from 'src/components/CrashChipPanel/crashCurve'
import { crashEventClockOriginMs } from 'src/components/CrashChipPanel/crashHold'

// NOTE: this event-clock multiplier matches the backend clock that settles
// cash-outs. It is the LOGIC input (climb gating, cash-out arming) and the
// fallback when no display loop is publishing — do NOT couple it to the
// frame-time beacon. The NUMBERS the player reads (hero + cash-out bar) come
// from the shared display clock instead (CrashChipPanel/useFrameBeacon
// `getDisplayMultiplier`, beacon-preferred): frames trail the backend, so the
// displayed multiplier is ≤ the one the backend actually pays — surprises
// only ever favor the player.
export function useCrashMultiplier(game: MarbleGame) {
  const [liveMultiplier, setLiveMultiplier] = useState(1.0)
  const animFrameRef = useRef<number>(0)

  const raceEvents = game.races[0].raceEvents
  const latestEvent = getLatestEvent(raceEvents)
  const isClimbing = latestEvent?.eventType === 'race_started'

  useEffect(() => {
    if (!isCrashGameType(game.track.gameType)) return

    if (!isClimbing) {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        animFrameRef.current = 0
      }
      setLiveMultiplier(1.0)
      return
    }

    // race_started's stamp, moved back by 3 − R on a short-hold round
    // (crashHold.ts): the climb is then R after race_started, as Go counts it.
    // At R 3 it is the stamp itself.
    const startTime = crashEventClockOriginMs(raceEvents)
    if (startTime === undefined) return

    const animate = () => {
      // Stay flat at 1.00x for the reveal-delay window, then climb. The offset
      // is subtracted from elapsed (clamped at 0) so the curve is identical to
      // the no-delay version, just shifted later by CRASH_REVEAL_DELAY_SECONDS.
      const effectiveElapsed = Math.max(
        0,
        (Date.now() - startTime) / 1000 - CRASH_REVEAL_DELAY_SECONDS
      )
      const mult = Math.exp(CRASH_RATE * effectiveElapsed)
      setLiveMultiplier(Math.round(mult * 100) / 100)
      animFrameRef.current = requestAnimationFrame(animate)
    }

    animFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        animFrameRef.current = 0
      }
    }
  }, [game.track.gameType, raceEvents, isClimbing])

  return { liveMultiplier, isClimbing }
}
